import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useT } from '../i18n';

/* SEO breadcrumbs — human trail + BreadcrumbList JSON-LD, page by page. */

const L = (en, es) => ({ en, es });

const SERVICES = { label: L('Services', 'Servicios'), href: '/services' };
const AI = { label: L('AI Solutions', 'Soluciones IA'), href: '/ai-solutions' };
const TECH = { label: L('Restaurant Technology', 'Tecnología para Restaurantes'), href: '/restaurant-technology' };
const PRESENCE = { label: L('Digital Presence', 'Presencia Digital'), href: '/digital-presence' };
const GROWTH = { label: L('Restaurant Growth', 'Crecimiento del Restaurante'), href: '/restaurant-growth' };
const INTEL = { label: L('Restaurant Intelligence', 'Inteligencia del Restaurante'), href: '/restaurant-intelligence' };
const MAGAZINE = { label: L('Resources', 'Recursos'), href: '/magazine' };

const MAP = {
  '/services': { label: SERVICES.label, parents: [] },
  '/ai-solutions': { label: AI.label, parents: [] },
  '/restaurant-technology': { label: TECH.label, parents: [SERVICES] },
  '/digital-presence': { label: PRESENCE.label, parents: [SERVICES] },
  '/restaurant-growth': { label: GROWTH.label, parents: [SERVICES] },
  '/restaurant-intelligence': { label: INTEL.label, parents: [SERVICES] },

  '/it-pos-systems': { label: L('IT & POS Systems', 'IT y Sistemas POS'), parents: [SERVICES, TECH] },
  '/services/pos-management': { label: L('POS Management', 'Gestión de POS'), parents: [SERVICES, TECH] },
  '/services/pos-integrations': { label: L('POS Integrations', 'Integraciones de POS'), parents: [SERVICES, TECH] },
  '/services/online-ordering': { label: L('Online Ordering', 'Pedidos Online'), parents: [SERVICES, TECH] },

  '/digital-branding': { label: L('Digital Branding', 'Branding Digital'), parents: [SERVICES, PRESENCE] },
  '/services/restaurant-websites': { label: L('Restaurant Websites', 'Sitios Web'), parents: [SERVICES, PRESENCE] },
  '/services/local-seo': { label: L('Local SEO', 'SEO Local'), parents: [SERVICES, PRESENCE] },
  '/services/google-business-profile': { label: L('Google Business Profile', 'Perfil de Google'), parents: [SERVICES, PRESENCE] },
  '/services/online-reputation': { label: L('Online Reputation', 'Reputación Online'), parents: [SERVICES, PRESENCE] },

  '/restaurant-revenue-growth': { label: L('Revenue Growth', 'Crecimiento de Ingresos'), parents: [SERVICES, GROWTH] },
  '/services/delivery-optimization': { label: L('Delivery Optimization', 'Optimización de Delivery'), parents: [SERVICES, GROWTH] },
  '/services/catering-growth': { label: L('Catering Growth', 'Crecimiento de Catering'), parents: [SERVICES, GROWTH] },
  '/services/customer-marketing': { label: L('Customer Marketing', 'Marketing al Cliente'), parents: [SERVICES, GROWTH] },

  '/services/data-analytics': { label: L('Data & Analytics', 'Datos y Analítica'), parents: [SERVICES, INTEL] },
  '/services/dashboards': { label: L('Dashboards', 'Dashboards'), parents: [SERVICES, INTEL] },
  '/services/performance-reporting': { label: L('Performance Reporting', 'Reportes de Rendimiento'), parents: [SERVICES, INTEL] },
  '/services/business-intelligence': { label: L('Business Intelligence', 'Business Intelligence'), parents: [SERVICES, INTEL] },

  '/index-ai': { label: L('Index AI', 'Index AI'), parents: [AI] },
  '/ai-agents-for-restaurants': { label: L('AI Agents', 'Agentes de IA'), parents: [AI] },
  '/ai-and-automation': { label: L('AI & Automation', 'IA y Automatización'), parents: [AI] },
  '/ai/custom-ai-automation': { label: L('Custom AI Automation', 'Automatización IA Personalizada'), parents: [AI] },
  '/ai/ai-receptionist': { label: L('AI Receptionist', 'Recepcionista IA'), parents: [AI] },
  '/ai/review-automation': { label: L('Review Automation', 'Automatización de Reseñas'), parents: [AI] },
  '/ai/catering-ai': { label: L('Catering AI', 'Catering AI'), parents: [AI] },

  '/how-it-works': { label: L('How It Works', 'Cómo Funciona'), parents: [] },
  '/pricing': { label: L('Pricing', 'Precios'), parents: [] },
  '/aboutus': { label: L('Company', 'Empresa'), parents: [] },
  '/faq': { label: L('FAQ', 'FAQ'), parents: [] },
  '/contact': { label: L('Contact', 'Contacto'), parents: [] },
  '/terms': { label: L('Terms of Service', 'Términos del Servicio'), parents: [] },
  '/privacy': { label: L('Privacy Policy', 'Política de Privacidad'), parents: [] },
  '/cookies': { label: L('Cookie Policy', 'Política de Cookies'), parents: [] },
  '/operating-intelligence': { label: L('Operating Intelligence', 'Operating Intelligence'), parents: [] },
  '/team': { label: L('Operating Intelligence', 'Operating Intelligence'), parents: [] },
  '/ai-team': { label: L('AI Team', 'Equipo IA'), parents: [] },
  '/magazine': { label: MAGAZINE.label, parents: [] },
};

const humanize = (slug) =>
  slug
    .split('-')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');

function resolveTrail(pathname, lang) {
  if (pathname === '/') return null;
  const clean = pathname.replace(/\/+$/, '') || '/';
  const pick = (l) => l[lang] || l.en;

  const entry = MAP[clean];
  if (entry) {
    return [
      ...entry.parents.map((p) => ({ label: pick(p.label), href: p.href })),
      { label: pick(entry.label), href: clean, current: true },
    ];
  }

  // Magazine dynamic routes
  if (clean.startsWith('/magazine/')) {
    const seg = clean.split('/').filter(Boolean);
    const last = humanize(seg[seg.length - 1]);
    return [
      { label: pick(MAGAZINE.label), href: '/magazine' },
      { label: last, href: clean, current: true },
    ];
  }

  // Fallback: humanized last segment
  const seg = clean.split('/').filter(Boolean);
  return [{ label: humanize(seg[seg.length - 1]), href: clean, current: true }];
}

const DARK_HERO = new Set([
  '/restaurant-technology',
  '/digital-presence',
  '/restaurant-growth',
  '/restaurant-intelligence',
  '/ai-solutions',
  '/services/online-ordering',
  '/services/restaurant-websites',
  '/services/data-analytics',
  '/index-ai',
  '/ai-agents-for-restaurants',
  '/operating-intelligence',
  '/team',
]);

export default function Breadcrumbs() {
  const { pathname } = useLocation();
  const { lang } = useT();
  const isEs = lang === 'es';
  const clean = pathname.replace(/\/+$/, '') || '/';
  const isAuthPage = clean === '/login' || clean === '/account' ||
    clean.startsWith('/affiliates/dashboard') || clean.startsWith('/admin');
  const trail = isAuthPage ? null : resolveTrail(pathname, lang);
  if (!trail) return null;

  const dark = DARK_HERO.has(pathname.replace(/\/+$/, '') || '/');
  const linkCls = dark
    ? 'text-neutral-400 hover:text-white transition-colors'
    : 'text-neutral-500 hover:text-neutral-900 transition-colors';
  const sepCls = dark ? 'text-neutral-600' : 'text-neutral-400';

  const homeLabel = isEs ? 'Inicio' : 'Home';
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: homeLabel, item: 'https://www.kitxens.com/' },
      ...trail.map((t, i) => ({
        '@type': 'ListItem',
        position: i + 2,
        name: t.label,
        item: `https://www.kitxens.com${t.href}`,
      })),
    ],
  };

  return (
    <div className="absolute top-[68px] left-0 right-0 z-30 pointer-events-none" data-testid="breadcrumbs-bar">
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(schema)}</script>
      </Helmet>
      <div className="max-w-[1280px] mx-auto px-6 lg:px-10">
        <nav
          aria-label="Breadcrumb"
          className="pointer-events-auto inline-flex max-w-full items-center overflow-x-auto whitespace-nowrap [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
        >
          <ol className="flex items-center gap-1.5 text-[12.5px]">
            <li className="flex items-center">
              <Link
                to="/"
                className={`font-medium ${linkCls}`}
                data-testid="breadcrumb-home"
              >
                {homeLabel}
              </Link>
            </li>
            {trail.map((t) => (
              <li key={t.href} className="flex items-center gap-1.5">
                <span className={`${sepCls} select-none`} aria-hidden="true">&gt;</span>
                {t.current ? (
                  <span
                    className="text-[#F77F11] font-semibold underline decoration-[#F77F11] decoration-2 underline-offset-4"
                    aria-current="page"
                    data-testid="breadcrumb-current"
                  >
                    {t.label}
                  </span>
                ) : (
                  <Link
                    to={t.href}
                    className={`font-medium ${linkCls}`}
                    data-testid={`breadcrumb-link-${t.href.replace(/\//g, '-').replace(/^-/, '')}`}
                  >
                    {t.label}
                  </Link>
                )}
              </li>
            ))}
          </ol>
        </nav>
      </div>
    </div>
  );
}
